import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, CheckCircle2, Circle, CalendarClock } from 'lucide-react';
import { API, fmtDate, daysUntil, inputCls } from './groupMeta';

export const GroupActionsTab = ({ group, getAuthHeaders, onChanged }) => {
  const [f, setF] = useState({ title: '', owner: '', due_date: '' });
  const [show, setShow] = useState('open');
  const canAct = group.can_inspect && group.status !== 'archived';
  const latest = group.inspections?.[0];
  const actions = (group.actions || []).filter(a => show === 'all' || a.status === show);
  const h = () => ({ headers: getAuthHeaders() });

  const add = async () => {
    if (!f.title.trim()) return toast.error('Describe the action');
    try {
      await axios.post(`${API}/groups/${group.id}/actions`, { ...f, due_date: f.due_date || null, inspection_id: latest?.id || null }, h());
      setF({ title: '', owner: '', due_date: '' }); toast.success('Action added'); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail?.toString() || 'Failed'); }
  };
  const toggle = async (a) => {
    try { await axios.patch(`${API}/actions/${a.id}`, { status: a.status === 'open' ? 'done' : 'open' }, h()); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const remove = async (a) => {
    try { await axios.delete(`${API}/actions/${a.id}`, h()); onChanged(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const total = group.actions?.length || 0;
  const done = group.actions?.filter(a => a.status === 'done').length || 0;
  return (
    <div className="space-y-4" data-testid="group-actions-tab">
      <div className="flex items-center gap-4">
        <div className="flex-1 h-2 rounded-full bg-white/[0.05] overflow-hidden"><div className="h-full bg-gradient-to-r from-emerald-400 to-cyan-400 transition-[width] duration-700" style={{ width: total ? `${(done / total) * 100}%` : 0 }} /></div>
        <span className="text-xs text-white/55 tabular-nums" data-testid="actions-progress">{done}/{total} complete</span>
      </div>
      {canAct && (
        <div className="grid md:grid-cols-[1fr_180px_160px_auto] gap-2">
          <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder={`Follow-up action${latest ? ` from ${fmtDate(latest.date)} inspection` : ''}…`} className={inputCls} data-testid="action-title-input" />
          <input value={f.owner} onChange={(e) => setF({ ...f, owner: e.target.value })} placeholder="Owner" className={inputCls} data-testid="action-owner-input" />
          <input type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} className={inputCls} data-testid="action-due-input" />
          <button onClick={add} data-testid="action-add-btn" className="inline-flex items-center justify-center gap-1 px-4 rounded-xl text-xs font-semibold text-[#0a0a14] bg-cyan-400"><Plus className="w-3.5 h-3.5" /> Add</button>
        </div>
      )}
      <div className="flex gap-1">
        {['open', 'done', 'all'].map(s => <button key={s} onClick={() => setShow(s)} data-testid={`actions-filter-${s}`}
          className={`px-3 py-1 rounded-full text-[11px] capitalize border ${show === s ? 'bg-cyan-500/15 border-cyan-400/40 text-cyan-100' : 'border-white/[0.07] text-white/50'}`}>{s}</button>)}
      </div>
      {actions.length === 0 ? <p className="text-sm text-white/40 py-10 text-center">No {show === 'all' ? '' : show} actions.</p> : (
        <div className="space-y-2">
          {actions.map(a => {
            const d = daysUntil(a.due_date);
            const late = a.status === 'open' && d !== null && d < 0;
            return (
              <div key={a.id} data-testid={`action-row-${a.id}`} className={`group flex items-center gap-3 px-4 py-3 rounded-xl border ${late ? 'border-rose-400/30 bg-rose-500/[0.04]' : 'border-white/[0.06] bg-white/[0.02]'}`}>
                <button disabled={!canAct} onClick={() => toggle(a)} data-testid={`action-toggle-${a.id}`}>
                  {a.status === 'done' ? <CheckCircle2 className="w-5 h-5 text-emerald-400" /> : <Circle className="w-5 h-5 text-white/30 hover:text-cyan-300" />}
                </button>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm ${a.status === 'done' ? 'text-white/40 line-through' : 'text-white'}`}>{a.title}</p>
                  <p className="text-[11px] text-white/40">{a.owner ? `Owner: ${a.owner}` : 'No owner'}{a.completed_at && a.status === 'done' ? ` · done ${fmtDate(a.completed_at)}` : ''}</p>
                </div>
                {a.due_date && <span className={`text-[11px] inline-flex items-center gap-1 ${late ? 'text-rose-300' : 'text-white/45'}`}><CalendarClock className="w-3.5 h-3.5" />{late ? `${-d}d late` : fmtDate(a.due_date)}</span>}
                {group.can_edit && group.status !== 'archived' && <button onClick={() => remove(a)} data-testid={`action-delete-${a.id}`} className="opacity-0 group-hover:opacity-100 p-1 text-white/40 hover:text-rose-300"><Trash2 className="w-3.5 h-3.5" /></button>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
